import { useMsal } from '@azure/msal-react';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useStores } from '../../common/hooks';
import { AssignedNominationStatus, AwardJudging } from '../../models';
import { RoutePaths } from '../../route-paths';
import { PageTitle } from '../shared-components';
import { CurrentWindow } from '../shared-components/CurrentWindow';
import Loading from '../shared-components/Loading';
import { Notifications } from '../shared-components/Notifications';
import { AwardJudgingBoxSection } from '../shared-components/award-judging-box-section/AwardJudgingBoxSection';
import NominationJudgedSummariesTable from '../shared-components/nomination-judged-summaries-table/NominationJudgedSummariesTable';
import './JudgingDashboardPage.scss';

const NO_NOMINATION_LEFT_TO_JUDGE_IN_AWARD = 'There are no nominations left for you to judge on this award at the moment. Please check back later.';
const NO_NOMINATION_LEFT_TO_JUDGE_IN_COUNTRY_AWARD = 'There are no nominations left for you to judge on this country award at the moment. Please check back later.'
const YOU_HAVE_COMPLETED_JUDGING_FOR_AWARD = 'You have completed your judging for this award. Thank you for your contribution!';
const YOU_HAVE_COMPLETED_JUDGING_FOR_COUNTRY_AWARD = 'You have completed your judging for this country award. Thank you for your contribution!';

const JudgingDashboardPageImpl: React.FC = () => {
  const {
    instance
  } = useMsal();

  const {
    apiStore,
    userStore,
    awardStore,
    notificationStore,
    judgingDashboardPageUiStore,
  } = useStores();

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const navigate = useNavigate();

  const handleAwardJudgingButtonClicked = async (awardJudging: AwardJudging): Promise<void> => {
    if (userStore.userId === undefined) return;

    judgingDashboardPageUiStore.clearAwardJudgingAlertMessage(awardJudging.awardId);

    setIsLoading(true);
    try {
      const nominationIdRef = await apiStore.getJudgeAssignedNomination(
        instance,
        awardJudging.awardId,
        userStore.userId,
      );

      switch (nominationIdRef.status) {
        case AssignedNominationStatus.Assigned:
          navigate(
            `${RoutePaths.Awards}/${awardJudging.awardId}${RoutePaths.Nominations}/${nominationIdRef.nominationId}`,
            { replace: true },
          );

          break;

        case AssignedNominationStatus.NoneAvailable:
          judgingDashboardPageUiStore.setAwardJudgingAlertMessage(
            awardJudging.awardId,
            NO_NOMINATION_LEFT_TO_JUDGE_IN_AWARD
          );

          break;

        case AssignedNominationStatus.JudgingComplete:
          judgingDashboardPageUiStore.setAwardJudgingAlertMessage(
            awardJudging.awardId,
            YOU_HAVE_COMPLETED_JUDGING_FOR_AWARD
          );

          break;

        default:
          return;
      }
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  }

  const handleCountryAwardJudgingButtonClicked = async (awardJudging: AwardJudging): Promise<void> => {
    if (userStore.userId === undefined || !awardJudging.countryId) return;

    judgingDashboardPageUiStore.clearCountryAwardJudgingAlertMessage(
      awardJudging.awardId,
      awardJudging.countryId,
    );

    setIsLoading(true);
    try {
      const nominationIdRef = await apiStore.getCountryJudgeAssignedNomination(
        instance,
        awardJudging.awardId,
        userStore.userId,
        awardJudging.countryId,
      );

      switch (nominationIdRef.status) {
        case AssignedNominationStatus.Assigned:
          navigate(
            `${RoutePaths.Awards}/${awardJudging.awardId}${RoutePaths.Nominations}/${nominationIdRef.nominationId}/${RoutePaths.Countries}/${awardJudging.countryId}`,
            { replace: true },
          );

          break;

        case AssignedNominationStatus.NoneAvailable:
          judgingDashboardPageUiStore.setCountryAwardJudgingAlertMessage(
            awardJudging.awardId,
            awardJudging.countryId,
            NO_NOMINATION_LEFT_TO_JUDGE_IN_COUNTRY_AWARD,
          );
          break;

        case AssignedNominationStatus.JudgingComplete:
          judgingDashboardPageUiStore.setCountryAwardJudgingAlertMessage(
            awardJudging.awardId,
            awardJudging.countryId,
            YOU_HAVE_COMPLETED_JUDGING_FOR_COUNTRY_AWARD,
          );
          break;

        default:
          return;
      }
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  }

  const getNotifications = async (programId: number): Promise<void> => {
    try {
      const notifications = await apiStore.getProgramNotifications(instance, programId, 'program-dashboard');
      notificationStore.setNotifications('program-dashboard', notifications);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getAwardJudgings = async (): Promise<void> => {
    if (userStore.userId === undefined) return;

    try {
      const awardJudgings = await apiStore.getAwardJudgings(instance, userStore.userId);
      judgingDashboardPageUiStore.setAwardJudgings(awardJudgings);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getNominationJudgedSummaries = async (): Promise<void> => {
    if (userStore.userId === undefined) return;

    try {
      const nominationJudgedSummaries = await apiStore.getNominationJudgedSummaries(instance, userStore.userId);
      judgingDashboardPageUiStore.setNominationJudgedSummaries(nominationJudgedSummaries);
    } catch (error) {
      // TODO - handle error
    }
  };

  const preloadData = async (): Promise<void> => {
    if (!awardStore.defaultProgramRef) return;

    setIsLoading(true);

    await Promise.allSettled([
      getNotifications(awardStore.defaultProgramRef.programId),
      awardStore.isJudgingWindowOpen && getAwardJudgings(),
      awardStore.isJudgingWindowOpen && getNominationJudgedSummaries(),
    ]);

    setIsLoading(false);
  };

  useEffect(() => {
    preloadData();
    return () => {
      judgingDashboardPageUiStore.resetStates();
    };
  }, []);

  return (
    <div className='judging-dashboard'>
      <PageTitle
        title={awardStore?.defaultProgramRef?.displayName ?? ''}
        subtitle='Microsoft AI Cloud Partner Program'
      />
      <CurrentWindow />
      <div className='judging-dashboard__main'>
        {isLoading && <Loading />}
        {
          userStore.isJudge && (
            <>
              {
                notificationStore.dashboardPageNotifications('program-dashboard').length > 0 &&
                <Notifications
                  notifications={notificationStore.dashboardPageNotifications('program-dashboard')}
                />
              }
              {
                awardStore.isJudgingWindowOpen &&
                <>
                  {
                    judgingDashboardPageUiStore.hasJudgings &&
                    <AwardJudgingBoxSection
                      awardJudgings={judgingDashboardPageUiStore.awardJudgings}
                      countryAwardJudgings={judgingDashboardPageUiStore.countryAwardJudgings}
                      awardAlertMessages={judgingDashboardPageUiStore.awardJudgingAlertMessages}
                      countryAwardAlertMessages={judgingDashboardPageUiStore.countryAwardJudgingAlertMessages}
                      handleAwardJudgingButtonClicked={handleAwardJudgingButtonClicked}
                      handleCountryAwardJudgingButtonClicked={handleCountryAwardJudgingButtonClicked}
                    />
                  }
                  {
                    judgingDashboardPageUiStore.hasNominationJudgedSummaries &&
                    <NominationJudgedSummariesTable
                      nominationJudgedSummaries={judgingDashboardPageUiStore.nominationJudgedSummaries}
                    />
                  }
                </>
              }
            </>
          )
        }
      </div>
    </div>
  );
}

export const JudgingDashboardPage = observer(JudgingDashboardPageImpl);
